import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useWhitelabel } from '../contexts/WhitelabelContext';
import { LANGUAGES, SUPPORTED_LOCALES } from '../i18n';
import { usersApi, whitelabelApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { 
  User, Shield, Palette, Bell, Lock, Users, Sun, Moon, Translate, PaintBrush
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getInitials } from '../lib/utils';
import PantonePalettePicker from '../components/PantonePalettePicker';
import logger from '../lib/logger';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const { config: wl, updateConfig: updateWl, refreshConfig } = useWhitelabel();
  const [wlForm, setWlForm] = useState({});
  const [wlSaving, setWlSaving] = useState(false);

  useEffect(() => {
    setWlForm({
      app_name: wl.app_name || '',
      app_subtitle: wl.app_subtitle || '',
      primary_color: wl.primary_color || '#0047FF',
      accent_color: wl.accent_color || '#10B981',
      custom_domain: wl.custom_domain || '',
      footer_text: wl.footer_text || '',
    });
  }, [wl]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await usersApi.getAll({ limit: 100 });
      setUsers(response.data);
    } catch (error) {
      logger.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersApi.updateRole(userId, newRole);
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-destructive text-white',
      manager: 'bg-primary text-white',
      user: 'bg-muted text-foreground'
    };
    return (
      <Badge className={colors[role] || colors.user}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">
            <User size={16} className="mr-2" />
            {t('settings.profile')}
          </TabsTrigger>
          <TabsTrigger value="appearance">
            <Palette size={16} className="mr-2" />
            {t('settings.appearance')}
          </TabsTrigger>
          <TabsTrigger value="language">
            <Translate size={16} className="mr-2" />
            {t('settings.language')}
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="whitelabel">
              <PaintBrush size={16} className="mr-2" />
              Branding
            </TabsTrigger>
          )}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <TabsTrigger value="users">
              <Users size={16} className="mr-2" />
              {t('settings.users')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h2 className="font-heading text-lg font-semibold mb-4">Profile Information</h2>
            <div className="flex items-start gap-6">
              <Avatar className="h-20 w-20 rounded-sm">
                {user?.picture ? (
                  <AvatarImage src={user.picture} alt={user.name} />
                ) : (
                  <AvatarFallback className="rounded-sm bg-primary/10 text-primary text-xl font-semibold">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                      Name
                    </Label>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                      Email
                    </Label>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                      Role
                    </Label>
                    <div>{getRoleBadge(user?.role)}</div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                      Member Since
                    </Label>
                    <p className="font-medium">{formatDate(user?.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border p-6 rounded-sm">
            <h2 className="font-heading text-lg font-semibold mb-4">Account Actions</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-destructive/5 border border-destructive/20 rounded-sm">
                <div>
                  <p className="font-medium">Sign Out</p>
                  <p className="text-sm text-muted-foreground">Sign out of your account on this device</p>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h2 className="font-heading text-lg font-semibold mb-4">Theme</h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-sm ${theme === 'light' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <Sun size={24} />
                </div>
                <div>
                  <p className="font-medium">Appearance</p>
                  <p className="text-sm text-muted-foreground">
                    Currently using {theme === 'light' ? 'Light' : 'Dark'} mode
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Sun size={18} className={theme === 'light' ? 'text-primary' : 'text-muted-foreground'} />
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                  data-testid="theme-switch"
                />
                <Moon size={18} className={theme === 'dark' ? 'text-primary' : 'text-muted-foreground'} />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border p-6 rounded-sm">
            <h2 className="font-heading text-lg font-semibold mb-4">Color Preview</h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <div className="h-12 w-full bg-primary rounded-sm"></div>
                <p className="text-xs text-center">Primary</p>
              </div>
              <div className="space-y-2">
                <div className="h-12 w-full bg-secondary rounded-sm"></div>
                <p className="text-xs text-center">Secondary</p>
              </div>
              <div className="space-y-2">
                <div className="h-12 w-full bg-success rounded-sm"></div>
                <p className="text-xs text-center">Success</p>
              </div>
              <div className="space-y-2">
                <div className="h-12 w-full bg-destructive rounded-sm"></div>
                <p className="text-xs text-center">Destructive</p>
              </div>
            </div>
          </div>

          <PantonePalettePicker />
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h2 className="font-heading text-lg font-semibold mb-2">{t('settings.selectLanguage')}</h2>
            <p className="text-sm text-muted-foreground mb-6">{t('settings.languageDesc')}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {LANGUAGES.filter(l => SUPPORTED_LOCALES.includes(l.code)).map((lang) => (
                <button
                  key={lang.code}
                  className={`p-4 rounded-sm border text-left transition-all ${
                    language === lang.code
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => { setLanguage(lang.code); toast.success(`Language changed to ${lang.name}`); }}
                  data-testid={`lang-${lang.code}`}
                >
                  <p className="font-semibold">{lang.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{lang.code.toUpperCase()} {lang.dir === 'rtl' ? '(RTL)' : ''}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border p-6 rounded-sm">
            <h2 className="font-heading text-lg font-semibold mb-2">Additional Languages</h2>
            <p className="text-sm text-muted-foreground mb-4">Coming soon - These languages use English as fallback</p>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {LANGUAGES.filter(l => !SUPPORTED_LOCALES.includes(l.code)).map((lang) => (
                <div key={lang.code} className="p-3 rounded-sm border border-border opacity-60">
                  <p className="text-sm font-medium">{lang.name}</p>
                  <p className="text-xs text-muted-foreground">{lang.code.toUpperCase()}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* White-label Tab (Admin only) */}
        {user?.role === 'admin' && (
          <TabsContent value="whitelabel" className="space-y-6">
            <div className="bg-card border border-border p-6 rounded-sm">
              <h2 className="font-heading text-lg font-semibold mb-4">Brand Customization</h2>
              <p className="text-sm text-muted-foreground mb-6">Customize the look and feel of your CRM. Changes apply instantly.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>App Name</Label>
                  <Input value={wlForm.app_name} onChange={(e) => setWlForm({ ...wlForm, app_name: e.target.value })} data-testid="wl-app-name" />
                </div>
                <div className="space-y-2">
                  <Label>Subtitle</Label>
                  <Input value={wlForm.app_subtitle} onChange={(e) => setWlForm({ ...wlForm, app_subtitle: e.target.value })} data-testid="wl-subtitle" />
                </div>
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={wlForm.primary_color} onChange={(e) => setWlForm({ ...wlForm, primary_color: e.target.value })} className="h-10 w-14 rounded-sm border border-border cursor-pointer" data-testid="wl-primary-color" />
                    <Input value={wlForm.primary_color} onChange={(e) => setWlForm({ ...wlForm, primary_color: e.target.value })} className="flex-1" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Accent Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={wlForm.accent_color} onChange={(e) => setWlForm({ ...wlForm, accent_color: e.target.value })} className="h-10 w-14 rounded-sm border border-border cursor-pointer" data-testid="wl-accent-color" />
                    <Input value={wlForm.accent_color} onChange={(e) => setWlForm({ ...wlForm, accent_color: e.target.value })} className="flex-1" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Custom Domain</Label>
                  <Input value={wlForm.custom_domain} onChange={(e) => setWlForm({ ...wlForm, custom_domain: e.target.value })} placeholder="crm.yourcompany.com" data-testid="wl-domain" />
                </div>
                <div className="space-y-2">
                  <Label>Footer Text</Label>
                  <Input value={wlForm.footer_text} onChange={(e) => setWlForm({ ...wlForm, footer_text: e.target.value })} placeholder="Powered by Your Company" data-testid="wl-footer" />
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <Button disabled={wlSaving} onClick={async () => {
                  setWlSaving(true);
                  const result = await updateWl(wlForm);
                  if (result.success) { toast.success('Branding updated'); refreshConfig(); }
                  else toast.error(result.error);
                  setWlSaving(false);
                }} data-testid="save-branding">
                  {wlSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setWlForm({
                  app_name: wl.app_name, app_subtitle: wl.app_subtitle,
                  primary_color: wl.primary_color, accent_color: wl.accent_color,
                  custom_domain: wl.custom_domain || '', footer_text: wl.footer_text || '',
                })}>Reset</Button>
              </div>
            </div>

            {/* Logo Upload */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h2 className="font-heading text-lg font-semibold mb-4">Logo</h2>
              <div className="flex items-center gap-6">
                {wl.logo_url ? (
                  <div className="h-16 w-16 rounded-sm bg-muted flex items-center justify-center overflow-hidden">
                    <img src={wl.logo_url} alt="Logo" className="h-full w-full object-contain" />
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded-sm bg-primary/10 flex items-center justify-center">
                    <PaintBrush size={24} className="text-primary" />
                  </div>
                )}
                <div>
                  <Input type="file" accept="image/*" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      await whitelabelApi.uploadLogo(formData);
                      toast.success('Logo uploaded');
                      refreshConfig();
                    } catch { toast.error('Upload failed'); }
                  }} data-testid="upload-logo" />
                  <p className="text-xs text-muted-foreground mt-1">Recommended: 256x256px, PNG or SVG</p>
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h2 className="font-heading text-lg font-semibold mb-4">Live Preview</h2>
              <div className="border border-border rounded-sm p-4 bg-muted/30">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
                  <div className="h-10 w-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: wlForm.primary_color + '20' }}>
                    <span className="font-bold text-lg" style={{ color: wlForm.primary_color }}>{(wlForm.app_name || 'N')[0]}</span>
                  </div>
                  <div>
                    <p className="font-heading font-bold">{wlForm.app_name || 'BIL'}</p>
                    <p className="text-xs text-muted-foreground">{wlForm.app_subtitle || 'Business Intelligence Lounge'}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="h-8 px-4 rounded-sm flex items-center text-white text-sm" style={{ backgroundColor: wlForm.primary_color }}>Primary Button</div>
                  <div className="h-8 px-4 rounded-sm flex items-center text-white text-sm" style={{ backgroundColor: wlForm.accent_color }}>Accent</div>
                </div>
                {wlForm.footer_text && (
                  <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">{wlForm.footer_text}</p>
                )}
              </div>
            </div>
          </TabsContent>
        )}

        {/* Users Tab (Admin/Manager only) */}
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <TabsContent value="users" className="space-y-6">
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-heading text-lg font-semibold">User Management</h2>
                <p className="text-sm text-muted-foreground">Manage user roles and permissions</p>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">User</th>
                    <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Email</th>
                    <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Role</th>
                    <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Joined</th>
                    {user?.role === 'admin' && (
                      <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loadingUsers ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground">Loading...</td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground">No users found</td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`user-row-${u.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9 rounded-sm">
                              {u.picture ? (
                                <AvatarImage src={u.picture} alt={u.name} />
                              ) : (
                                <AvatarFallback className="rounded-sm bg-primary/10 text-primary font-medium">
                                  {getInitials(u.name)}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm">{u.email}</td>
                        <td className="p-4">{getRoleBadge(u.role)}</td>
                        <td className="p-4 text-sm text-muted-foreground">{formatDate(u.created_at)}</td>
                        {user?.role === 'admin' && (
                          <td className="p-4">
                            <div className="flex justify-end">
                              {u.id !== user.id ? (
                                <Select
                                  value={u.role}
                                  onValueChange={(value) => handleRoleChange(u.id, value)}
                                >
                                  <SelectTrigger className="w-32" data-testid={`role-select-${u.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="user">User</SelectItem>
                                    <SelectItem value="manager">Manager</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-muted-foreground">Current user</span>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
